/* eslint-disable @typescript-eslint/no-explicit-any */
import { Address } from '@/types/company';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  formatPhoneNumber as fmtNational,
  formatPhoneNumberIntl as fmtIntl,
} from 'react-phone-number-input';
import { format as formatDateFns } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import {
  conversationTypeColorMap,
  conversationTimelineColorMap,
  conversationCardBorderColorMap,
} from '@/constants/color';
import { conversationTimelineIcon, conversationTypeIconMap } from '@/constants/icons';
import { PillProps } from '../components/ui/Pill';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type DateFormat = 'default' | 'month-year';

const toTimezoneDate = (date: Date) => {
  const timezone = getCurrentTimezone();

  try {
    return toZonedTime(date, timezone);
  } catch {
    return date;
  }
};

const lowerMeridiem = (value: string) => value.replace('AM', 'am').replace('PM', 'pm');

export const normalizeTimestamp = (value: string | number | Date) => {
  if (value instanceof Date) return value;

  const isNumeric = typeof value === 'number' || /^\d+$/.test(String(value));
  if (!isNumeric) return value;

  const timestamp = Number(value);
  if (timestamp >= 1e17) return Math.floor(timestamp / 1_000_000);
  if (timestamp >= 1e14) return Math.floor(timestamp / 1_000);
  return timestamp;
};

export function formatDate(
  date: string | number | Date | null | undefined,
  showTime = false,
  format: DateFormat | { pattern: string; timeZone?: string } = 'default'
) {
  if (date === null || date === undefined || date === '') {
    return '-';
  }

  let processedDate: string | number | Date = normalizeTimestamp(date);
  const isDateOnlyString = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
  if (isDateOnlyString) {
    processedDate = date.replace(/-/g, '/');
  }

  const d = new Date(processedDate);
  if (Number.isNaN(d.getTime())) {
    return '-';
  }

  if (typeof format === 'object') {
    return isDateOnlyString
      ? formatDateFns(d, format.pattern)
      : formatInTimeZone(d, format.timeZone ?? getCurrentTimezone(), format.pattern);
  }

  if (format === 'month-year') {
    return isDateOnlyString
      ? formatDateFns(d, 'MMMM yyyy')
      : formatInTimeZone(d, getCurrentTimezone(), 'MMMM yyyy');
  }

  if (!showTime) {
    return isDateOnlyString
      ? formatDateFns(d, 'MMM dd, yyyy')
      : formatInTimeZone(d, getCurrentTimezone(), 'MMM dd, yyyy');
  }

  return lowerMeridiem(
    isDateOnlyString
      ? formatDateFns(d, 'MMM dd, yyyy, h:mm a')
      : formatInTimeZone(d, getCurrentTimezone(), 'MMM dd, yyyy, h:mm a')
  );
}

export function formatDateAtStartOfDay(date: string | number | Date) {
  return formatDateFns(new Date(date), "yyyy-MM-dd'T'00:00:00");
}

export function formatTime(date: string | number | Date | null | undefined) {
  if (date === null || date === undefined || date === '') {
    return '-';
  }

  let processedDate = date;
  if (typeof date === 'number' && date > 1e12) {
    processedDate = Math.floor(date / 1000000);
  }

  const d = new Date(processedDate);
  if (Number.isNaN(d.getTime())) {
    return '-';
  }

  return lowerMeridiem(formatInTimeZone(d, getCurrentTimezone(), 'h:mm a'));
}

export function isDefaultDate(date?: string | null) {
  return date === '1970-01-01T00:00:00Z';
}

export function jsonToMarkdownTable(jsonArray: Record<string, any>[]): string {
  if (jsonArray.length === 0) return '';

  // Extract headers
  const headers = Object.keys(jsonArray[0]);

  // Create Markdown header row
  let table = `| ${headers.join(' | ')} |\n`;
  table += `|${headers.map(() => '----------------').join('|')}|\n`;

  // Add rows
  jsonArray.forEach(record => {
    const row = headers
      .map(header => {
        const originalValue = record[header as keyof Record<string, any>];
        if (header === 'Page URL' && originalValue.toString().length > 20) {
          return `[${originalValue.toString().slice(0, 20) + '...'}](# "${originalValue}")`;
        }
        if (originalValue === null || originalValue === undefined) return 'null';
        return originalValue.toString();
      })
      .join(' | ');
    table += `| ${row} |\n`;
  });

  return table;
}

export function formatPhoneNumber(phoneNumberString: string) {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return null;
}

/**
 * Format an E.164 phone number for display, e.g. "+1 (202) 555-1234".
 * Falls back to the international format when the national format includes a trunk
 * prefix (leading 0 common in India, UK, etc.) to avoid e.g. "+91 099525 78899".
 */
export function formatPhoneDisplay(phone: string): string {
  if (!phone) return phone;
  const intl = fmtIntl(phone); // "+1 202 555 1234" | "+91 99525 78899"
  const national = fmtNational(phone); // "(202) 555-1234" | "099525 78899"
  if (!intl) return phone;
  if (!national) return intl;
  const countryCode = intl.split(' ')[0]; // "+1" | "+91"
  const intlSubscriberDigits = intl.replace(/\D/g, '').length - countryCode.replace('+', '').length;
  const nationalDigits = national.replace(/\D/g, '').length;
  // National format includes a trunk prefix if it has more digits than the subscriber number
  if (nationalDigits > intlSubscriberDigits) {
    return intl; // "+91 99525 78899" — no trunk prefix
  }
  return `${countryCode} ${national}`; // "+1 (202) 555-1234"
}

export function objectPathSet(path: string, value: any, existing: Record<string, any> = {}) {
  const keys = path.split('.');
  const obj: Record<string, any> = existing || {};
  keys.reduce((acc, key, index) => {
    if (!acc) {
      acc = {};
    }
    if (!acc[key]) {
      acc[key] = {};
    }
    if (index === keys.length - 1) {
      acc[key] = value;
    }
    return acc[key];
  }, obj);
  return obj;
}

export function objectPathGet(obj: Record<string, any>, path: string) {
  const keys = path.split('.');
  const value = keys.reduce((acc, key) => {
    if (acc[key] === undefined) {
      return {};
    }
    return acc[key];
  }, obj);
  return value as unknown;
}

export function nanoSecondsToSeconds(nanoSeconds: number) {
  return Math.floor(nanoSeconds / 1_000_000);
}

export function formatAddress(address: Address) {
  if (!address) return '-';
  const parts = [
    address.line1 || '',
    address.line2 || '',
    address.city || '',
    address.state || '',
    address.country || '',
    address.postal_code || '',
  ].filter(Boolean);

  return parts.join(', ');
}

export function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined || isNaN(num)) return '';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export const formatAmount = (
  value: string | number | null | undefined,
  options: { compact?: boolean; fractionDigits?: number } = {}
) => {
  const num = Number(value);
  if (num === null || num === undefined || isNaN(num)) return '-';
  if (options.compact === false) {
    return `$${num.toLocaleString('en-US', {
      minimumFractionDigits: options.fractionDigits ?? 0,
      maximumFractionDigits: options.fractionDigits ?? 0,
    })}`;
  }
  if (num >= 1e9) {
    return `$${Math.round(num / 1e9)}B`;
  } else if (num >= 1e6) {
    return `$${Math.round(num / 1e6)}M`;
  } else if (num >= 1e3) {
    return `$${Math.round(num / 1e3)}K`;
  }
  return `$${Math.round(num)}`;
};

const getValueByPath = (value: unknown, path: string[]): unknown => {
  return path.reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object' || !(key in current)) {
      return undefined;
    }
    return (current as Record<string, unknown>)[key];
  }, value);
};

const getStringValue = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  return undefined;
};

export const getErrorMessage = (error: unknown, fallbackMessage = 'Something went wrong') => {
  const backendMessagePaths = [
    ['response', 'data', 'error', 'message'],
    ['response', 'data', 'message'],
    ['response', 'data', 'error'],
    ['data', 'error', 'message'],
    ['data', 'message'],
    ['data', 'error'],
    ['error', 'message'],
    ['error'],
    ['cause', 'error', 'message'],
    ['cause', 'message'],
  ];

  for (const path of backendMessagePaths) {
    const message = getStringValue(getValueByPath(error, path));
    if (message) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  return fallbackMessage;
};

export const getVariableFromString = (
  message: string
): { position: number; variable: string }[] => {
  const regex = /{{(.*?)}}/g;
  const variables = [];
  let match;
  let position = 0;

  while ((match = regex.exec(message)) !== null) {
    variables.push({
      position: position++,
      variable: match[1],
    });
  }

  return variables;
};

export function formatRelativeDate(dateStr: string, alwaysRelative = false): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (minutes < 60) {
    return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
  }

  if (hours < 24) {
    return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  }

  if (alwaysRelative) {
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;

    const years = Math.floor(days / 365);
    return `${years} year${years === 1 ? '' : 's'} ago`;
  }

  return formatDate(dateStr, true);
}

export function jsonToQueryString(json: Record<string, any>) {
  const params = [];
  for (const key in json) {
    if (json.hasOwnProperty(key)) {
      const encodedKey = encodeURIComponent(key);
      const encodedValue = encodeURIComponent(json[key]);
      params.push(`${encodedKey}=${encodedValue}`);
    }
  }
  return params.join('&');
}

export const convertTime12to24 = (time12h: string) => {
  const [time, modifier] = time12h.split(' ');

  const [hoursString, minutesString] = time.split(':');

  let hours = 0;
  if (hoursString === '12') {
    hours = 0;
  } else {
    hours = parseInt(hoursString, 10);
  }

  if (modifier === 'pm') {
    hours = hours + 12;
  }

  return {
    hours,
    minutes: parseInt(minutesString, 10),
  };
};

export function normalizeTimezone(timeZone: string) {
  if (timeZone === 'Asia/Calcutta') {
    return 'Asia/Kolkata';
  }

  return timeZone;
}

let currentUserViewTimezone: string | undefined;

export function setCurrentUserViewTimezone(timeZone?: string | null) {
  currentUserViewTimezone = timeZone ? normalizeTimezone(timeZone) : undefined;
}

export function getBrowserTimezone() {
  const timeZone =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'America/Los_Angeles';

  return normalizeTimezone(timeZone);
}

export function getCurrentTimezone() {
  if (currentUserViewTimezone) {
    return currentUserViewTimezone;
  }

  return getBrowserTimezone();
}

export function formatDateGroup(date: string | Date | number | null | undefined, showTime = false) {
  if (date === null || date === undefined || date === '') {
    return '-';
  }

  let processedDate = date;
  const isDateOnlyString = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
  if (isDateOnlyString) {
    // Force local date parsing for YYYY-MM-DD strings
    processedDate = date.replace(/-/g, '/');
  }
  const d = new Date(processedDate);
  if (Number.isNaN(d.getTime())) {
    return '-';
  }

  const displayDate = isDateOnlyString ? d : toTimezoneDate(d);
  const displayToday = toTimezoneDate(new Date());
  const displayYesterday = new Date(displayToday);
  displayYesterday.setDate(displayToday.getDate() - 1);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(displayDate, displayToday)) {
    if (showTime) {
      return 'Today ' + formatDateFns(displayDate, 'hh:mm a').toLowerCase();
    }
    return 'Today';
  }
  if (isSameDay(displayDate, displayYesterday)) {
    if (showTime) {
      return 'Yesterday ' + formatDateFns(displayDate, 'hh:mm a').toLowerCase();
    }
    return `Yesterday`;
  }

  if (showTime) {
    return (
      formatDateFns(displayDate, 'MMM d, yyyy') +
      ' ' +
      formatDateFns(displayDate, 'hh:mm a').toLowerCase()
    );
  }

  return `${formatDateFns(displayDate, 'MMM d, yyyy')}`;
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getConversationTypeColor = (type: string, direction: string, status: string) => {
  if (!type) return 'neutral';
  if (status === 'not_delivered') return 'neutral';
  let colorKey = 'neutral';
  if (status === 'review' || status === 'draft') colorKey = `${type}-draft`;
  else colorKey = `${type}-${direction}`;
  return conversationTypeColorMap[
    colorKey as keyof typeof conversationTypeColorMap
  ] as PillProps['color'];
};

export const getConversationBorderColor = (type: string, direction: string, status: string) => {
  if (!type) return 'grey';
  let colorKey = 'bg-transparent';
  if (status === 'review' || status === 'draft') colorKey = `${type}-draft`;
  else colorKey = `${type}-${direction}`;
  return conversationCardBorderColorMap[
    colorKey as keyof typeof conversationCardBorderColorMap
  ] as PillProps['color'];
};

export const getConversationTimelineColor = (type: string, direction: string, status: string) => {
  if (!type) return 'grey';
  if (status === 'not_delivered') return 'grey';
  let colorKey = 'grey';
  if (status === 'review' || status === 'draft') colorKey = `${type}-draft`;
  else colorKey = `${type}-${direction}`;
  return conversationTimelineColorMap[
    colorKey as keyof typeof conversationTimelineColorMap
  ] as PillProps['color'];
};

export const getConversationTypeIcon = (type: string, direction: string, status: string) => {
  let iconKey = 'email-draft';
  if (status === 'review' || status === 'draft') iconKey = `${type}-draft`;
  else iconKey = `${type}-${direction}`;
  return conversationTypeIconMap[iconKey as keyof typeof conversationTypeIconMap];
};

export const getConversationTimelineIcon = (type: string, direction: string, status: string) => {
  let iconKey = 'email-draft';
  if (status === 'review' || status === 'draft') iconKey = `${type}-draft`;
  else iconKey = `${type}-${direction}`;
  return conversationTimelineIcon[iconKey as keyof typeof conversationTimelineIcon];
};

export function renderMessageBody(body: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(body, 'text/html');
  doc.querySelectorAll('a[href*="unsubscribe"]').forEach(a => a.remove());
  doc.querySelectorAll('span[data-type="mention"]').forEach(span => {
    span.classList.remove('text-primary', 'font-medium');
    if (span.classList.length === 0) span.removeAttribute('class');
  });
  return doc.body.innerHTML.replaceAll(/<a/g, '<a target="_blank"').replace(/\n/g, '<br />');
}

import type { Granularity, PeriodRange } from '@/types/admin';
export type { Granularity, PeriodRange };

export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getPeriodRange(g: Granularity, offset: number, now = new Date()): PeriodRange {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (g === 'day') {
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
    return { start, end: new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1) };
  }
  if (g === 'week') {
    const monday = today.getDate() - ((today.getDay() + 6) % 7);
    const start = new Date(today.getFullYear(), today.getMonth(), monday + offset * 7);
    return { start, end: new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7) };
  }
  if (g === 'month') {
    const start = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    return { start, end: new Date(start.getFullYear(), start.getMonth() + 1, 1) };
  }
  const start = new Date(today.getFullYear() + offset, 0, 1);
  return { start, end: new Date(start.getFullYear() + 1, 0, 1) };
}
